// client/src/components/DIVERS/EmailVerification.js
import React, { useState, useEffect, useContext, useRef } from 'react';
import { sendEmailVerification } from 'firebase/auth';
import { auth } from '../../firebase';
import { AuthContext } from '../../AuthContext';
import { FiMail } from 'react-icons/fi';
import { BrandMark } from '../shared/BrandLogo';

const RESEND_COOLDOWN = 60; // secondes
const POLL_INTERVAL   = 3000; // vérification auto toutes les 3s

export default function EmailVerification() {
  const { reloadEmailVerified, userRole } = useContext(AuthContext);

  const [status, setStatus]       = useState(null); // { text, ok }
  const [sending, setSending]     = useState(false);
  const [checking, setChecking]   = useState(false);
  const [cooldown, setCooldown]   = useState(0);
  const cooldownRef = useRef(null);
  const pollRef     = useRef(null);
  // Pour les commerçants en cours d'inscription, on pointe vers une page neutre
  // (pas de redirection automatique vers l'app après vérification email)
  const continueUrl = useRef(
    userRole === 'commercantpending'
      ? `${window.location.origin}/email-verified`
      : window.location.href
  );

  const sendVerificationEmail = async () => {
    if (sending || cooldown > 0) return;
    setSending(true);
    setStatus(null);
    try {
      await sendEmailVerification(auth.currentUser, { url: continueUrl.current });
      setStatus({ text: 'Email envoyé ! Consultez votre boîte mail.', ok: true });
      setCooldown(RESEND_COOLDOWN);
    } catch (err) {
      if (err.code === 'auth/too-many-requests') {
        setStatus({ text: 'Email envoyé ! Consultez votre boîte mail.', ok: true });
        setCooldown(RESEND_COOLDOWN);
      } else {
        setStatus({ text: 'Impossible d\'envoyer l\'email. Réessayez.', ok: false });
      }
    } finally {
      setSending(false);
    }
  };

  // Envoi automatique au montage
  useEffect(() => {
    sendVerificationEmail();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Décompte du cooldown
  useEffect(() => {
    if (cooldown <= 0) return;
    cooldownRef.current = setInterval(() => {
      setCooldown((c) => {
        if (c <= 1) { clearInterval(cooldownRef.current); return 0; }
        return c - 1;
      });
    }, 1000);
    return () => clearInterval(cooldownRef.current);
  }, [cooldown]);

  // Polling automatique : vérifie toutes les 3s si l'email a été validé
  useEffect(() => {
    pollRef.current = setInterval(async () => {
      if (!auth.currentUser) return;
      await reloadEmailVerified();
      // Si emailVerified passe à true, AuthContext met à jour le state
      // et App.js retire ce composant automatiquement
    }, POLL_INTERVAL);
    return () => clearInterval(pollRef.current);
  }, [reloadEmailVerified]);

  // Bouton secours : vérification manuelle
  const handleCheckVerification = async () => {
    if (checking) return;
    setChecking(true);
    setStatus(null);
    try {
      await reloadEmailVerified();
      if (!auth.currentUser?.emailVerified) {
        setStatus({ text: 'Lien pas encore cliqué. Vérifiez votre boîte mail.', ok: false });
      }
    } finally {
      setChecking(false);
    }
  };

  const handleLogout = async () => {
    try { await auth.signOut(); } catch {}
  };

  const resendDisabled = sending || cooldown > 0;

  return (
    <div className="eb-auth">
      <div className="eb-auth__card" style={{ textAlign: 'center' }}>
        <div className="eb-auth__brand"><BrandMark to={null} /></div>

        <div style={iconWrap} aria-hidden="true"><FiMail /></div>
        <h1 className="eb-auth__title">Vérifiez votre email</h1>
        <p className="eb-auth__subtitle" style={{ marginBottom: 24, lineHeight: 1.6 }}>
          Un lien de vérification a été envoyé à{' '}
          <strong style={{ color: 'var(--eb-text)', fontWeight: 600, wordBreak: 'break-word' }}>{auth.currentUser?.email}</strong>.
        </p>

        <div className="eb-auth__form">
          {status && (
            <div className={`eb-alert ${status.ok ? 'eb-alert--success' : 'eb-alert--danger'}`} role="status" style={{ textAlign: 'left' }}>
              {status.text}
            </div>
          )}

          <button
            type="button"
            className="eb-btn eb-btn--primary eb-btn--lg eb-btn--block"
            onClick={handleCheckVerification}
            disabled={checking}
          >
            {checking ? <span className="eb-spinner eb-spinner--sm eb-spinner--light" aria-hidden="true" /> : null}
            {checking ? 'Vérification…' : 'J\'ai cliqué sur le lien'}
          </button>

          <button
            type="button"
            className="eb-btn eb-btn--secondary eb-btn--lg eb-btn--block"
            onClick={sendVerificationEmail}
            disabled={resendDisabled}
          >
            {sending
              ? 'Envoi en cours…'
              : cooldown > 0
              ? `Renvoyer l'email (${cooldown}s)`
              : 'Renvoyer l\'email de vérification'}
          </button>
        </div>

        <p style={{ color: 'var(--eb-muted)', fontSize: 13.5, lineHeight: 1.55, margin: '20px 0 0' }}>
          Vous ne trouvez pas l'email ?<br />Pensez à vérifier votre dossier <strong style={{ color: 'var(--eb-text-2)', fontWeight: 600 }}>Spam / Courrier indésirable</strong>.
        </p>

        <div className="eb-auth__footer">
          <button
            type="button"
            onClick={handleLogout}
            className="eb-link"
            style={{ color: 'var(--eb-danger)', minHeight: 40 }}
          >
            Se déconnecter
          </button>
        </div>
      </div>
    </div>
  );
}

const iconWrap = {
  width: 56, height: 56, margin: '0 auto 16px', borderRadius: '50%',
  display: 'flex', alignItems: 'center', justifyContent: 'center',
  background: 'var(--eb-brand-50)', border: '1px solid var(--eb-brand-100)',
  color: 'var(--eb-brand)', fontSize: 24,
};
